import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Migration } from '../../types';
import { ui } from '../../config/uiSizes';
import { useFetchJobs } from '../../hooks/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../components/Toast/ToastProvider';
import { formatUtcTimestampLocal } from '../../utils/formatters';
import { downloadHistoricalSnapshot } from '../../services/Snapshot/snapshotHistoryService';
import { logger } from '../../utils/logger';
import Spinner from '../../components/migratorDashboard/Spinner';
import {
    flattenSnapshots,
    filterSnapshots,
    getSnapshotOrgOptions,
    formatSnapshotSize,
    type SnapshotHubRow,
} from '../../utils/snapshotHub';

/**
 * Global Snapshot Hub.
 *
 * Browses every snapshot captured across all migrations from one place — with
 * org and date filters — instead of opening each job individually. Each row
 * previews the snapshot's size and file count and offers Download and Compare
 * actions (Compare reuses the existing Snapshot Comparison Tool, scoped to the
 * snapshot's own migration).
 */
const SnapshotHubPage: React.FC = () => {
    const navigate = useNavigate();
    const { accessToken } = useAuth();
    const { showToast } = useToast();
    const { jobs, loading } = useFetchJobs();

    const [orgFilter, setOrgFilter] = React.useState('');
    const [fromDate, setFromDate] = React.useState('');
    const [toDate, setToDate] = React.useState('');
    const [downloadingPath, setDownloadingPath] = React.useState<string | null>(null);

    const allRows = React.useMemo(() => flattenSnapshots(jobs), [jobs]);
    const orgOptions = React.useMemo(() => getSnapshotOrgOptions(allRows), [allRows]);
    const rows = React.useMemo(
        () =>
            filterSnapshots(allRows, {
                orgId: orgFilter || undefined,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
            }),
        [allRows, orgFilter, fromDate, toDate]
    );

    const jobsById = React.useMemo(() => {
        const map = new Map<string, Migration>();
        for (const job of jobs) map.set(job.id, job);
        return map;
    }, [jobs]);

    const handleCompare = (row: SnapshotHubRow) => {
        const job = jobsById.get(row.migrationId);
        if (!job) return;
        navigate('/SnapshotComparisonTool', { state: { job, modalVariant: 'view' } });
    };

    const handleDownload = async (row: SnapshotHubRow) => {
        if (!accessToken) {
            showToast('You must be signed in to download a snapshot', 'error');
            return;
        }
        const timestamp = row.blobPath.split('/').pop() ?? '';
        try {
            setDownloadingPath(row.blobPath);
            await downloadHistoricalSnapshot(accessToken, row.migrationId, timestamp);
            showToast('Snapshot downloaded successfully', 'success');
        } catch (error) {
            logger.error(
                'Error downloading snapshot from hub',
                error instanceof Error ? error : new Error(String(error))
            );
            showToast('Failed to download snapshot', 'error');
        } finally {
            setDownloadingPath(null);
        }
    };

    const clearFilters = () => {
        setOrgFilter('');
        setFromDate('');
        setToDate('');
    };
    const hasActiveFilter = Boolean(orgFilter || fromDate || toDate);

    const statusBadgeClasses = (status: string): string => {
        const base = `${ui.badge.sm} rounded font-medium`;
        switch (status.toLowerCase()) {
            case 'complete':
            case 'completed':
                return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
            case 'failed':
                return `${base} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
            default:
                return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
        }
    };

    return (
        <div className={`${ui.page.padding}`}>
            <div className={`${ui.mb.section}`}>
                <h1 className={`${ui.text.pageTitle} font-bold text-gray-900 dark:text-gray-100`}>
                    Snapshot Hub
                </h1>
                <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                    Browse and compare every snapshot captured across your migrations in one place.
                </p>
            </div>

            {/* Filters */}
            <div
                className={`${ui.page.sectionPadding} ${ui.mb.control} bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg flex flex-col sm:flex-row flex-wrap items-start sm:items-end ${ui.gap.md}`}
            >
                <div className="flex flex-col">
                    <label className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300 mb-1`}>
                        Organization
                    </label>
                    <select
                        value={orgFilter}
                        onChange={(e) => setOrgFilter(e.target.value)}
                        className={`${ui.input.formDropdown} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                        <option value="">All organizations</option>
                        {orgOptions.map((org) => (
                            <option key={org.id} value={org.id}>
                                {org.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col">
                    <label className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300 mb-1`}>
                        From
                    </label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={`${ui.input.custom} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                </div>

                <div className="flex flex-col">
                    <label className={`${ui.text.label} font-semibold text-gray-700 dark:text-gray-300 mb-1`}>
                        To
                    </label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={`${ui.input.custom} border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                </div>

                {hasActiveFilter && (
                    <button
                        type="button"
                        onClick={clearFilters}
                        className={`${ui.button.sm} text-blue-600 dark:text-blue-400 font-semibold hover:underline`}
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {loading ? (
                <div className={`flex items-center justify-center ${ui.gap.md} py-10`}>
                    <Spinner />
                    <span className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>Loading snapshots…</span>
                </div>
            ) : rows.length === 0 ? (
                <div
                    className={`${ui.page.cardPadding} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center`}
                >
                    <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                        {allRows.length === 0
                            ? 'No snapshots have been captured yet.'
                            : 'No snapshots match the selected filters.'}
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>Captured</th>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>Migration</th>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>Organization</th>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>Status</th>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>Files</th>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>Size</th>
                                <th className={`${ui.text.tableHeader} font-semibold text-gray-700 dark:text-gray-300 ${ui.page.rowPadding} text-right`}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={`${row.migrationId}-${row.blobPath}`}
                                    className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                >
                                    <td className={`${ui.text.tableCell} text-gray-900 dark:text-gray-100 ${ui.page.rowPadding} whitespace-nowrap`}>
                                        {formatUtcTimestampLocal(row.capturedAtUtc)}
                                    </td>
                                    <td className={`${ui.text.tableCell} text-gray-900 dark:text-gray-100 ${ui.page.rowPadding}`}>
                                        {row.migrationName}
                                    </td>
                                    <td className={`${ui.text.tableCell} text-gray-700 dark:text-gray-300 ${ui.page.rowPadding}`}>
                                        {row.orgName}
                                    </td>
                                    <td className={`${ui.page.rowPadding}`}>
                                        <span className={statusBadgeClasses(row.status)}>{row.status}</span>
                                    </td>
                                    <td className={`${ui.text.tableCell} text-gray-700 dark:text-gray-300 ${ui.page.rowPadding} whitespace-nowrap`}>
                                        {row.fileCount ?? '—'}
                                    </td>
                                    <td className={`${ui.text.tableCell} text-gray-700 dark:text-gray-300 ${ui.page.rowPadding} whitespace-nowrap`}>
                                        {formatSnapshotSize(row.totalBytes)}
                                    </td>
                                    <td className={`${ui.page.rowPadding}`}>
                                        <div className={`flex items-center justify-end ${ui.gap.sm}`}>
                                            <button
                                                type="button"
                                                onClick={() => handleCompare(row)}
                                                className={`${ui.button.sm} border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium`}
                                            >
                                                Compare
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDownload(row)}
                                                disabled={downloadingPath === row.blobPath}
                                                className={`${ui.button.sm} bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white rounded-md font-medium disabled:opacity-60`}
                                            >
                                                {downloadingPath === row.blobPath ? 'Downloading…' : 'Download'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SnapshotHubPage;
